import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Stack,
  Button,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import AdminSidebar from "../../Components/AdminPanel/AdminSidebar";

toast.configure();

const TransitBranchChange = () => {
  const userToken = LocalstorageHelper.GetAdminToken();

  // ✅ default MANUAL
  const [autoMode, setAutoMode] = useState(false);

  // ✅ finyear
  const [finyears, setFinyears] = useState([]);
  const [loadingFinyears, setLoadingFinyears] = useState(false);
  const [finyear, setFinyear] = useState(null);
  const finid = Number(finyear?.finyearid || 0);

  // manual invno
  const [invnoInput, setInvnoInput] = useState("");

  // auto invno
  const [invnoSelected, setInvnoSelected] = useState(null);
  const [invnoAutoText, setInvnoAutoText] = useState("");
  const [invnoOptions, setInvnoOptions] = useState([]);
  const [loadingInvno, setLoadingInvno] = useState(false);

  // branches (to branch list)
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [toBranch, setToBranch] = useState(null);

  // header + rows
  const [header, setHeader] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // ✅ correct invno for both modes
  const invDisplay = String(
    autoMode ? (invnoSelected || invnoAutoText) : invnoInput
  ).trim();

  const columns = useMemo(
    () => [
      { field: "invno", headerName: "Inv No", flex: 1, minWidth: 150 },
      { field: "invdate", headerName: "Inv Date", width: 105 },
      { field: "partno", headerName: "Part No", flex: 1, minWidth: 140 },
      { field: "qty", headerName: "Qty", width: 80, type: "number" },
      { field: "rate", headerName: "Rate", width: 95, type: "number" },
      { field: "value", headerName: "Value", width: 110, type: "number" },
    ],
    []
  );

  // ---------------------------
  // API calls (relative URLs)
  // ---------------------------

  const fetchFinyears = useCallback(async () => {
    if (!userToken) return;
    try {
      setLoadingFinyears(true);
      const r = await fetch("/admin/transit/branchchange/finyears", {
        method: "GET",
        headers: { token: userToken },
      });
      const j = await r.json();
      if (j.type !== "success") {
        toast.error(j.message || "Failed to load finyears");
        setFinyears([]);
        setFinyear(null);
        return;
      }

      const list = j.data || [];
      setFinyears(list);

      // ✅ auto pick active if exists
      const activeOne = list.find((x) => x.active);
      setFinyear(activeOne || list[0] || null);
    } catch (e) {
      toast.error("Failed to load finyears");
      setFinyears([]);
      setFinyear(null);
    } finally {
      setLoadingFinyears(false);
    }
  }, [userToken]);

  const fetchBranches = useCallback(async () => {
    if (!userToken) return;
    try {
      setLoadingBranches(true);
      const r = await fetch("/admin/transit/branchchange/branches", {
        method: "GET",
        headers: { token: userToken },
      });
      const j = await r.json();
      if (j.type !== "success") {
        toast.error(j.message || "Failed to load branches");
        setBranches([]);
        return;
      }
      setBranches(j.data || []);
    } catch (e) {
      toast.error("Failed to load branches");
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  }, [userToken]);

  const fetchInvnoOptions = useCallback(
    async (q) => {
      if (!userToken) return;
      if (!finid) return;

      try {
        setLoadingInvno(true);
        const r = await fetch(
          `/admin/transit/branchchange/invnos/${finid}?q=${encodeURIComponent(q || "")}`,
          { method: "GET", headers: { token: userToken } }
        );
        const j = await r.json();
        if (j.type !== "success") {
          setInvnoOptions([]);
          return;
        }
        setInvnoOptions(j.data || []);
      } catch (e) {
        setInvnoOptions([]);
      } finally {
        setLoadingInvno(false);
      }
    },
    [userToken, finid]
  );

  const fetchDetails = useCallback(
    async (invno) => {
      const v = String(invno || "").trim();
      if (!v) return;

      if (!userToken) return;
      if (!finid) return toast.warn("Select Finyear first");

      try {
        setLoadingDetails(true);
        setHeader(null);
        setRows([]);
        setToBranch(null);

        const r = await fetch(
          `/admin/transit/branchchange/details/${finid}?invno=${encodeURIComponent(v)}`,
          { method: "GET", headers: { token: userToken } }
        );
        const j = await r.json();

        if (j.type !== "success") {
          toast.error(j.message || "No data found");
          return;
        }

        setHeader(j.header || null);
        setRows((j.rows || []).map((x) => ({ id: x.transitid, ...x })));
      } catch (e) {
        toast.error("Failed to fetch transit details");
      } finally {
        setLoadingDetails(false);
      }
    },
    [userToken, finid]
  );

  // ---------------------------
  // lifecycle
  // ---------------------------
  useEffect(() => {
    fetchFinyears();
    fetchBranches();
  }, [fetchFinyears, fetchBranches]);

  // When finyear changes: clear results + refresh invno options (only if auto)
  useEffect(() => {
    setHeader(null);
    setRows([]);
    setToBranch(null);
    setInvnoSelected(null);
    setInvnoOptions([]);

    // keep input values, but you can clear if you want
    if (autoMode && finid) fetchInvnoOptions("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finid]);

  // Auto mode typing -> fetch invnos
  useEffect(() => {
    if (!autoMode) return;
    if (!userToken || !finid) return;

    const t = setTimeout(() => fetchInvnoOptions(invnoAutoText), 250);
    return () => clearTimeout(t);
  }, [autoMode, invnoAutoText, fetchInvnoOptions, userToken, finid]);

  // ---------------------------
  // actions
  // ---------------------------
  const onSearchClick = async () => {
    if (!finid) return toast.warn("Select Finyear first");
    if (!invDisplay) return toast.warn("Enter invno");
    await fetchDetails(invDisplay);
  };

  const onUpdateClick = () => {
    if (!finid) return toast.warn("Select Finyear first");
    if (!header) return toast.warn("Search invno first");
    if (!toBranch) return toast.warn("Select To Branch");

    const fromId = Number(header.frombranchid || 0);
    const toId = Number(toBranch.branchid || 0);
    if (!fromId || !toId) return toast.warn("Branch not valid");
    if (fromId === toId) return toast.warn("From branch and To branch are same");

    setConfirmOpen(true);
  };

  const doUpdate = async () => {
    if (!userToken) return;
    if (!finid) return toast.warn("Select Finyear first");

    try {
      setUpdating(true);

      const payload = {
        invno: invDisplay,
        tobranchid: Number(toBranch?.branchid || 0),
      };

      const r = await fetch(`/admin/transit/branchchange/update/${finid}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: userToken },
        body: JSON.stringify(payload),
      });

      const j = await r.json();
      if (j.type !== "success") {
        toast.error(j.message || "Update failed");
        return;
      }

      toast.success(j.message || "Updated!");
      setConfirmOpen(false);
      await fetchDetails(invDisplay);
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setUpdating(false);
    }
  };

  // ✅ safe early return after hooks
  if (!userToken) return <Redirect exact to="/" />;

  return (
    <>
      <AdminSidebar />

      <Box sx={{ p: 1.2 }}>
        <Paper sx={{ p: 1.2, mb: 1, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>
                Transit Branch Change
              </Typography>

              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={autoMode}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setAutoMode(v);

                      setHeader(null);
                      setRows([]);
                      setToBranch(null);

                      setInvnoSelected(null);
                      setInvnoOptions([]);
                      setInvnoAutoText("");

                      // keep manual input (optional)
                      // setInvnoInput("");

                      if (v) {
                        if (!finid) toast.warn("Select Finyear first");
                        else setTimeout(() => fetchInvnoOptions(""), 0);
                      }
                    }}
                  />
                }
                label={<Typography variant="caption">{autoMode ? "Auto" : "Manual"}</Typography>}
              />
            </Stack>

            <Divider />

            {/* ✅ Finyear */}
            <Autocomplete
              fullWidth
              size="small"
              options={finyears}
              loading={loadingFinyears}
              value={finyear}
              onChange={(e, v) => setFinyear(v)}
              getOptionLabel={(o) =>
                o
                  ? `${o.finname}${o.active ? " (Active)" : ""}${
                      o.fromdate ? `  [${o.fromdate} - ${o.todate}]` : ""
                    }`
                  : ""
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Finyear"
                  placeholder="Select finyear..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingFinyears ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Invno + search */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
              {autoMode ? (
                <Autocomplete
                  fullWidth
                  size="small"
                  options={invnoOptions}
                  value={invnoSelected}
                  onChange={(e, v) => setInvnoSelected(v)}
                  inputValue={invnoAutoText}
                  onInputChange={(e, v) => setInvnoAutoText(v)}
                  loading={loadingInvno}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Inv No"
                      placeholder="Type invno..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingInvno ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Inv No"
                  value={invnoInput}
                  onChange={(e) => setInvnoInput(e.target.value)}
                  placeholder="Enter invno..."
                />
              )}

              <Button
                size="small"
                variant="contained"
                onClick={onSearchClick}
                disabled={loadingDetails}
                sx={{ minWidth: 110, height: 36 }}
              >
                {loadingDetails ? "Loading..." : "Search"}
              </Button>
            </Stack>

            {/* header */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                label="Company"
                value={header?.companyname || ""}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                size="small"
                label="From Branch (Logged-in)"
                value={header?.frombranchname || ""}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Stack>

            {/* To branch + update */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
              <Autocomplete
                fullWidth
                size="small"
                options={branches}
                loading={loadingBranches}
                value={toBranch}
                onChange={(e, v) => setToBranch(v)}
                getOptionLabel={(o) => (o ? `${o.branchname} (${o.branchid})` : "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="To Branch"
                    placeholder="Select branch..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={onUpdateClick}
                disabled={!header || !toBranch || updating}
                sx={{ minWidth: 140, height: 36 }}
              >
                {updating ? "Updating..." : "Update"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Grid */}
        <Paper sx={{ p: 0.5, borderRadius: 2 }}>
          <Box sx={{ height: 430, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              disableRowSelectionOnClick
              density="compact"
              loading={loadingDetails}
              rowHeight={30}
              columnHeaderHeight={34}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* confirm */}
      <Dialog open={confirmOpen} onClose={() => (!updating ? setConfirmOpen(false) : null)}>
        <DialogTitle sx={{ py: 1 }}>Confirm</DialogTitle>
        <DialogContent dividers sx={{ py: 1 }}>
          <Typography variant="body2">
            Change InvNo <b>{invDisplay}</b> from <b>{header?.frombranchname || "?"}</b> to{" "}
            <b>{toBranch?.branchname || "?"}</b> ?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Finyear: <b>{finyear?.finname || "-"}</b>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button size="small" disabled={updating} onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={updating}
            onClick={doUpdate}
          >
            {updating ? "Updating..." : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransitBranchChange;
